import { Component, GondelBaseComponent } from '@gondel/core';

// core version + navigation, pagination modules:
import { Swiper} from 'swiper/bundle';
import { getGridDefinition, SliderTypes } from './swiper-definitions';

import * as LightBox from '../../../molecules/lightbox-gallery/js/lightbox-gallery.js';


/**
 * slider module implementation.
 * @author Srdan Turudic <srdan.turudic@namics.com>
 */

enum Selectors {
	Slider = '.cms-slider__slider',
	// ESLint does recognize the import and this property as the same so no-shadow needs to be disabled
	 
	Pagination = '.js-slider__pagination',
	PrevButton = '.js-slider__button-prev',
	NextButton = '.js-slider__button-next',
	Scrollbar = '.swiper-scrollbar',
	SlideOverlay = '.swiper-slide__overlay',
}

enum States {
	SlideOverlayVisible = 'state-cms-slider__slider__slide--inactive',
}

enum CustomAttributes {
	SliderType = 'data-slider-type',
}

@Component('Slider')
export class Slider extends GondelBaseComponent {
	private static _slidersCount = 0;

	private _swiper: Swiper | undefined;
	private _sliderType: SliderTypes = '' as SliderTypes;

	public static get SlidersCount() {
		this._slidersCount += 1;
		return this._slidersCount;
	}

	public start() {
		this._sliderType = this._ctx.getAttribute(CustomAttributes.SliderType) as SliderTypes;
		const accessKeys: any = this._appendSliderItemSuffixes();

		const swip = new Swiper(accessKeys[Selectors.Slider], {
			// Optional parameters
			direction: 'horizontal',
			loop: false,
			spaceBetween: 24,
			breakpoints: getGridDefinition(this._sliderType),

			// Navigation arrows
			navigation: {
				nextEl: accessKeys[Selectors.NextButton],
				prevEl: accessKeys[Selectors.PrevButton],
			},

			// Pagination
			...(this._sliderType === SliderTypes.ImageGallery && {
				pagination: {
					el: accessKeys[Selectors.Pagination],
					type: 'bullets',
					clickable: true,
				},
			}),
		});

		
		swip.slides.forEach((x) => this._toggleOverlay(x));
		swip.on('resize', () => swip.slides.forEach((x) => this._toggleOverlay(x)));
		swip.on('slideChange', () => swip.slides.forEach((x) => this._toggleOverlay(x)));
		
		this._swiper = swip;

		if (this._sliderType === SliderTypes.ImageGallery) {
			LightBox.initPhotoSwipeFromDOM(LightBox.selectors.lightboxGallery);
		}
	}

	/**
	 * Toggles the overlay for all slides that are outside of the possible y elements in a grid per breakpoint
	 * @param slide
	 */
	private _toggleOverlay(slide: HTMLElement): void {
		if (!this._swiper) return;
		const elementIndex = this._swiper.slides.indexOf(slide);
		const activeIndex = this._swiper.activeIndex;
		const lastActiveIndex = activeIndex + (Number(this._swiper.params.slidesPerView) - 1);

		if (elementIndex >= activeIndex && elementIndex <= lastActiveIndex) {
			slide?.classList.remove(States.SlideOverlayVisible);
			this._enableSlideTabIndex(slide as HTMLElement, true);
		} else {
			slide?.classList.add(States.SlideOverlayVisible);
			this._enableSlideTabIndex(slide as HTMLElement, false);
		}
	}

	private _enableSlideTabIndex(slide: HTMLElement, enable: boolean): any {
		const slideTabElements: NodeListOf<HTMLElement> = slide.querySelectorAll('a, button');
		slideTabElements?.forEach((element) => (element.tabIndex = enable ? 0 : -1));
	}

	private _appendSliderItemSuffixes(): any {
		const index = Slider.SlidersCount;

		return {
			[Selectors.Slider]: this._appendSliderTypeSuffix(Selectors.Slider, index),
			[Selectors.Pagination]: this._appendSliderTypeSuffix(Selectors.Pagination, index),
			[Selectors.PrevButton]: this._appendSliderTypeSuffix(Selectors.PrevButton, index),
			[Selectors.NextButton]: this._appendSliderTypeSuffix(Selectors.NextButton, index),
		};
	}

	/**
	 * Append the slider type suffix for a selector. Hence swiper.js searches all elements by class from the DOM,
	 * multiple sliders on the same page will not work as expected. Slider specific elements need to be specified by slider type with a class and modifier.
	 * Example: cms-slider__slider--testimonial
	 * Read further: https://codepen.io/intotheprogram/pen/BaaeyxM
	 * Answer on stackoverflow: https://stackoverflow.com/a/48984056
	 * @param selector
	 * @returns string
	 */
	private _appendSliderTypeSuffix(selector: Selectors, index: number): string {
		const sliderSpecificClassName = selector.concat('--', this._sliderType, '-', index.toString());
		this._ctx.querySelector(selector)?.classList.add(sliderSpecificClassName.substring(1));
		return sliderSpecificClassName;
	}
}
